import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import {
  countMonetarySecurity,
  countPriveeSecurity,
  countPublicSecurity,
} from '../../service/api/SecurityService';
import { getTotalCashPublic } from '../../service/api/TransactionService';
import CountersSkeletons from '../../skeleton/CountersSkeletons';

const TitleGraphe = () => {
  const selectedDomain = useSelectedDomain();
  const [data, setData] = useState({
    totalPricePublic: null,
    totalPrice: null,
    totalPriceMonetary: null,
    totalPriceCash: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedDomain?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [publicData, privateData, monetaryData, cashData] = await Promise.all([
          countPublicSecurity().then(res => res?.totalPrice ?? 0),
          countPriveeSecurity().then(res => res?.totalPrice ?? 0),
          countMonetarySecurity().then(res => res?.totalPrice ?? 0),
          getTotalCashPublic(selectedDomain.id).then(res => res?.totalCash ?? 0),
        ]);

        setData({
          totalPricePublic: publicData,
          totalPrice: privateData,
          totalPriceMonetary: monetaryData,
          totalPriceCash: cashData,
        });
      } catch (err) {
        console.error('Erreur chargement données titres:', err);
        setError(err.message);
        toast.error('Erreur lors du chargement des données des titres.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDomain]);

  // Afficher le skeleton pendant le chargement
  if (loading) {
    return <CountersSkeletons count={4} showYearBadge={false} />;
  }

  // Afficher un message d'erreur si nécessaire
  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        <strong>Erreur:</strong> Impossible de charger les données des titres.
      </div>
    );
  }

  const cards = [
    {
      title: 'Titres Publics',
      value: data.totalPricePublic,
      logo: 'https://www.bvm-ac.org/wp-content/uploads/elementor/thumbs/BVMAC-Logo-BA.fw_-ogk7i5vvwnmff84b2u6wo9rd6pa19yy4j24u796rsa.png',
      alt: 'BVMAC Logo'
    },
    {
      title: 'Titres Privés',
      value: data.totalPrice,
      logo: 'https://caa.cm/wp-content/uploads/2025/01/logo-caa.png',
      alt: 'CAA Logo'
    },
    {
      title: 'Titres Monétaires',
      value: data.totalPriceMonetary,
      logo: 'https://www.beac.int/wp-content/themes/WPBlank-master/img/beac-logo.png',
      alt: 'BEAC Logo'
    },
    {
      title: 'Total AUC',
      value: data.totalPriceCash,
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#4A90E2" strokeWidth="2" />
          <path d="M12 2A10 10 0 0 1 22 12H12V2Z" fill="#4A90E2" />
        </svg>
      )
    }
  ];

  return (
    <>
      <style>{`
        .title-graphe-container {
          padding: 0.5rem 0;
        }

        .title-graphe-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .card.counter {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border: 1px solid #e9ecef;
          border-radius: 16px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          padding: 1.5rem 1rem;
          text-align: center;
          transition: all 0.3s ease;
          animation: fadeInCard 0.5s ease-out;
        }

        .card.counter:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.1);
          border-color: #182848;
        }

        .counter-card-body {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }

        .counter-card-icon {
          margin-bottom: 0.5rem;
        }

        .counter-card-logo {
          width: 60px;
          height: 60px;
          object-fit: contain;
          filter: grayscale(0.3);
          transition: filter 0.3s ease;
        }

        .card.counter:hover .counter-card-logo {
          filter: grayscale(0);
        }

        .counter-card-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: #2c3e50;
          margin: 0;
          text-align: center;
        }

        .counter-card-amount-container {
          margin-top: 0.5rem;
        }

        .counter-card-amount {
          margin: 0;
        }

        .counter-amount-badge {
          background: linear-gradient(135deg, #4A90E2, #357ABD);
          color: white;
          border-radius: 50px;
          padding: 0.5rem 1rem;
          font-size: 0.85rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3);
          display: inline-block;
          white-space: nowrap;
        }

        .counter-card-icon svg {
          transition: transform 0.3s ease;
        }

        .card.counter:hover .counter-card-icon svg {
          transform: scale(1.1);
        }

        @keyframes fadeInCard {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .title-graphe-cards-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
        }

        @media (max-width: 480px) {
          .title-graphe-cards-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="title-graphe-container">
        <div className="title-graphe-cards-grid">
          {cards.map((card, index) => (
            <div className="counter-card-wrapper" key={index}>
              <div className="card counter">
                <div className="counter-card-body">
                  <div className="counter-card-icon">
                    {card.logo ? (
                      <img 
                        src={card.logo} 
                        alt={card.alt} 
                        className="counter-card-logo" 
                      />
                    ) : (
                      card.icon
                    )}
                  </div>
                  <p className="counter-card-title">{card.title}</p>
                  <div className="counter-card-amount-container">
                    <h2 className="counter-card-amount">
                      <span className="counter-amount-badge">
                        {new Intl.NumberFormat("fr-FR").format(card.value || 0)} FCFA
                      </span>
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default TitleGraphe;